/* Doctor Compliance Escalations Inbox — Phase 4 of Compliance Monitoring & Support.
 *
 * Lists every escalation raised against the logged-in doctor's cases by
 * their assigned compliance coaches. Doctor can read context, then
 * resolve with an optional reply note that the coach will see inline in
 * their own escalations panel.
 *
 * Backend endpoints:
 *   GET  /doctor/compliance-escalations              — full list
 *   GET  /doctor/compliance-escalations?status=open  — filtered
 *   POST /doctor/compliance-escalations/{id}/resolve — close with note
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Warning, ShieldWarning, ArrowsClockwise, CheckCircle, Clock, Headset,
    ChatCircle, ArrowSquareOut, ChatTeardropDots, PaperPlaneTilt, X,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";


function relativeIso(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch { return "—"; }
}


export default function DoctorEscalationsInbox() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("open");
    const [active, setActive] = useState(null); // selected escalation for resolve dialog

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/doctor/compliance-escalations");
            setRows(r.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const { openItems, resolvedItems } = useMemo(() => {
        const o = []; const r = [];
        for (const x of rows) (x.status === "open" ? o : r).push(x);
        return { openItems: o, resolvedItems: r };
    }, [rows]);

    const visible = tab === "open" ? openItems : resolvedItems;

    return (
        <div className="space-y-6" data-testid="doctor-escalations-inbox">
            {/* Header — match /doctor/patients + /doctor/cases grammar */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Escalations</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Patient-side concerns your Invisique™ compliance coaches have flagged for your attention. Resolve with a note — the coach sees your reply inline.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={load}
                    data-testid="doctor-escalations-refresh"
                >
                    <ArrowsClockwise size={13} className="mr-1" /> Refresh
                </Button>
            </div>

            {/* Summary tiles — standard StatCard pattern (label • icon chip • big value • sub) */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Tile
                    label="Open"
                    value={openItems.length}
                    tone="rose"
                    icon={ShieldWarning}
                    helper="Awaiting your response"
                    testId="tile-open"
                />
                <Tile
                    label="Resolved"
                    value={resolvedItems.length}
                    tone="emerald"
                    icon={CheckCircle}
                    helper="Closed by you"
                    testId="tile-resolved"
                />
                <Tile
                    label="All-time"
                    value={rows.length}
                    tone="primary"
                    icon={Clock}
                    helper="Total escalations"
                    testId="tile-all"
                />
            </div>

            {/* Sub-tabs — solid teal pill style (matches /doctor/billing + /doctor/cases) */}
            <div
                className="inline-flex bg-card border border-border rounded-xl p-1 gap-0.5"
                role="tablist"
                data-testid="escalations-tabs"
            >
                <Tab
                    active={tab === "open"}
                    onClick={() => setTab("open")}
                    label="Open"
                    count={openItems.length}
                    testId="tab-open"
                />
                <Tab
                    active={tab === "resolved"}
                    onClick={() => setTab("resolved")}
                    label="Resolved"
                    count={resolvedItems.length}
                    testId="tab-resolved"
                />
            </div>

            {/* List */}
            {loading ? (
                <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
            ) : visible.length === 0 ? (
                <EmptyList tab={tab} />
            ) : (
                <ul className="space-y-3">
                    {visible.map((e) => (
                        <EscalationCard
                            key={e.id}
                            esc={e}
                            onResolveClick={() => setActive(e)}
                            onCommentSaved={(updated) => {
                                setRows((arr) => arr.map((r) => (r.id === updated.id ? updated : r)));
                            }}
                        />
                    ))}
                </ul>
            )}

            {active && (
                <ResolveDialog
                    esc={active}
                    onClose={() => setActive(null)}
                    onResolved={(updated) => {
                        setRows((arr) => arr.map((r) => (r.id === updated.id ? updated : r)));
                        setActive(null);
                        toast.success("Escalation resolved");
                    }}
                />
            )}
        </div>
    );
}


function Tile({ label, value, tone, helper, icon: Icon, testId }) {
    // Matches the shared StatCard visual grammar from /doctor/offers +
    // /doctor/appointments + /doctor/patients: neutral card + tinted icon
    // chip + big value + one-line helper. Kept the tone arg so the value
    // colour still communicates urgency.
    const palette = {
        amber:   { chip: "bg-amber-50 text-amber-700",     val: "text-amber-700" },
        emerald: { chip: "bg-emerald-50 text-emerald-700", val: "text-emerald-700" },
        primary: { chip: "bg-primary/10 text-primary",     val: "text-primary" },
    }[tone] || { chip: "bg-secondary text-foreground/70", val: "text-foreground" };
    return (
        <div className="bg-card border border-border rounded-2xl p-4" data-testid={testId}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                {Icon && (
                    <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${palette.chip}`}>
                        <Icon size={14} weight="duotone" />
                    </span>
                )}
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${palette.val}`}>{value}</div>
            {helper && <div className="text-[11px] text-muted-foreground mt-1.5">{helper}</div>}
        </div>
    );
}


function Tab({ active, onClick, label, count, testId }) {
    // Solid teal pill — matches /doctor/billing + /doctor/cases sub-tabs.
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                active
                    ? "bg-primary text-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            }`}
        >
            {label}
            <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums min-w-[1.75rem] inline-block text-center ${
                    active ? "bg-white/25 text-white" : "bg-secondary text-muted-foreground"
                }`}
            >
                {count}
            </span>
        </button>
    );
}


function EscalationCard({ esc, onResolveClick, onCommentSaved }) {
    const isOpen = esc.status === "open";
    const navigate = useNavigate();
    const [replyOpen, setReplyOpen] = useState(false);
    const comments = esc.comments || [];

    const openCase = () => navigate(`/doctor/cases/${esc.case_id}`);
    const messagePatient = () => {
        if (!esc.patient_id) {
            toast.error("Patient context not available for this escalation");
            return;
        }
        navigate(`/doctor/patient-chat?patient=${esc.patient_id}`);
    };

    return (
        <li
            className={`rounded-2xl border p-5 transition ${
                isOpen ? "bg-rose-50/40 border-rose-200" : "bg-emerald-50/40 border-emerald-200"
            }`}
            data-testid={`escalation-${esc.id}`}
        >
            <div className="flex items-start gap-4">
                {/* iter98.99ed — Anonymized CoE badge. Doctors see which CoE
                    function raised the concern, never the individual coach's
                    name or photo. */}
                <div className="shrink-0">
                    <div
                        className="w-10 h-10 rounded-full grid place-items-center text-white ring-1 ring-border"
                        style={{ background: "linear-gradient(135deg, hsl(180 50% 45%), hsl(200 60% 50%))" }}
                        data-testid={`escalation-coe-badge-${esc.id}`}
                        title="Raised by an Invisique™ CoE team member"
                    >
                        <Headset size={16} weight="duotone" />
                    </div>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        {/* iter102.22 — Aligned to canonical escalation
                            status colours used across every other portal:
                              open      → rose (interpersonal ping)
                              resolved  → emerald (closed loop)
                            Doctor was the outlier at amber; every PES
                            surface + SupportTickets + ComplianceCase
                            item all use rose. Label capitalized to match. */}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${
                            isOpen ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"
                        }`}>
                            {isOpen ? <Warning size={10} weight="fill" /> : <CheckCircle size={10} weight="fill" />}
                            {isOpen ? "Open" : "Resolved"}
                        </span>
                        <Link
                            to={`/doctor/cases/${esc.case_id}`}
                            className="text-[12px] font-mono text-primary hover:underline"
                            data-testid={`escalation-case-link-${esc.id}`}
                        >
                            {esc.case_code || "case"}
                        </Link>
                        {esc.patient_name && (
                            <span className="text-[12px] text-muted-foreground">· {esc.patient_name}</span>
                        )}
                        <span className="text-[11px] text-muted-foreground ml-auto inline-flex items-center gap-1">
                            <Clock size={11} />{relativeIso(esc.created_at)}
                        </span>
                    </div>
                    <div className="text-sm leading-relaxed text-foreground">{esc.note}</div>
                    <div className="text-[11px] text-muted-foreground mt-2" data-testid={`escalation-raised-by-${esc.id}`}>
                        Raised by <span className="font-medium text-foreground">{esc.raised_by_role_label || "Invisique™ CoE team"}</span>
                    </div>

                    {/* iter98.99ee — Multi-turn comments thread. Doctor and
                        coach replies render here in chronological order. */}
                    {comments.length > 0 && (
                        <ul className="mt-4 space-y-2" data-testid={`escalation-thread-${esc.id}`}>
                            {comments.map((c) => (
                                <CommentBubble key={c.id} comment={c} />
                            ))}
                        </ul>
                    )}

                    {!isOpen && esc.resolved_note && (
                        <div className="mt-3 rounded-lg bg-emerald-100/70 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-900">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1">Your resolution</div>
                            {esc.resolved_note}
                            <div className="text-[10px] text-emerald-700/80 mt-1">Resolved {relativeIso(esc.resolved_at)}</div>
                        </div>
                    )}

                    {/* iter98.99ee — Tier 1 action toolbar. Reply lets the
                        doctor add a comment without closing. Open Case +
                        Message Patient give one-click navigation. Resolve
                        stays as the terminal action. */}
                    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`escalation-actions-${esc.id}`}>
                        {isOpen && (
                            <button
                                type="button"
                                onClick={() => setReplyOpen((v) => !v)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium border transition ${
                                    replyOpen
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-white text-foreground border-border hover:bg-secondary/40"
                                }`}
                                data-testid={`escalation-reply-toggle-${esc.id}`}
                            >
                                {replyOpen ? <X size={12} weight="bold" /> : <ChatCircle size={12} weight="fill" />}
                                {replyOpen ? "Cancel" : "Reply"}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={openCase}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-foreground border border-border hover:bg-secondary/40 text-[12.5px] font-medium transition"
                            data-testid={`escalation-open-case-${esc.id}`}
                            title="Open the full case in a new view"
                        >
                            <ArrowSquareOut size={12} weight="bold" /> Open case
                        </button>
                        <button
                            type="button"
                            onClick={messagePatient}
                            disabled={!esc.patient_id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-foreground border border-border hover:bg-secondary/40 disabled:opacity-50 disabled:cursor-not-allowed text-[12.5px] font-medium transition"
                            data-testid={`escalation-message-patient-${esc.id}`}
                            title="Open a direct chat with this patient"
                        >
                            <ChatTeardropDots size={12} weight="fill" /> Message patient
                        </button>
                        {isOpen && (
                            <button
                                type="button"
                                onClick={onResolveClick}
                                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-medium transition"
                                data-testid={`escalation-resolve-btn-${esc.id}`}
                            >
                                <CheckCircle size={13} weight="fill" /> Resolve
                            </button>
                        )}
                    </div>

                    {replyOpen && isOpen && (
                        <ReplyComposer
                            escalationId={esc.id}
                            onCancel={() => setReplyOpen(false)}
                            onSaved={(updated) => {
                                setReplyOpen(false);
                                onCommentSaved?.(updated);
                                toast.success("Reply sent to the CoE team");
                            }}
                        />
                    )}
                </div>
            </div>
        </li>
    );
}


/* iter98.99ee — Comment bubble inside the escalation thread. Doctor's own
   replies hug the right edge (sent-style); CoE replies hug the left
   (received-style). Anonymizes CoE author with the role label. */
function CommentBubble({ comment }) {
    const isDoctor = comment.author_role === "doctor";
    const author = isDoctor
        ? "You"
        : (comment.author_role_label || "Invisique™ CoE team");
    return (
        <li
            className={`flex ${isDoctor ? "justify-end" : "justify-start"}`}
            data-testid={`escalation-comment-${comment.id}`}
        >
            <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                    isDoctor
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-white border border-border rounded-bl-md"
                }`}
            >
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isDoctor ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {author} · {relativeIso(comment.created_at)}
                </div>
                <div className="whitespace-pre-wrap">{comment.text}</div>
            </div>
        </li>
    );
}


/* iter98.99ee — Inline composer. Doctor types, presses Send (or Cmd/Ctrl-Enter),
   we POST the comment, the parent refreshes the card with the returned
   anonymized payload (so doctor sees the CoE role label on coach replies). */
function ReplyComposer({ escalationId, onCancel, onSaved }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        const t = text.trim();
        if (!t) return;
        setSending(true);
        try {
            const r = await api.post(`/doctor/compliance-escalations/${escalationId}/comment`, { text: t });
            onSaved?.(r.data);
            setText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send reply");
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className="mt-3 rounded-xl border border-primary/30 bg-white p-3" data-testid={`escalation-reply-composer-${escalationId}`}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                rows={3}
                placeholder="Reply to the CoE team (Cmd/Ctrl+Enter to send)…"
                className="w-full resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                data-testid={`escalation-reply-input-${escalationId}`}
                autoFocus
            />
            <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel} type="button">Cancel</Button>
                <Button
                    type="button"
                    size="sm"
                    onClick={submit}
                    disabled={sending || !text.trim()}
                    className="bg-primary hover:bg-primary/90"
                    data-testid={`escalation-reply-send-${escalationId}`}
                >
                    <PaperPlaneTilt size={12} weight="fill" className="mr-1.5" />
                    {sending ? "Sending…" : "Send"}
                </Button>
            </div>
        </div>
    );
}


function EmptyList({ tab }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-3">
                <CheckCircle size={24} className="text-emerald-700" weight="duotone" />
            </div>
            <div className="font-display text-base font-semibold">
                {tab === "open" ? "All clear" : "No resolved escalations yet"}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">
                {tab === "open"
                    ? "No open escalations from your compliance coaches. They'll show up here the moment a coach flags a patient concern that needs your input."
                    : "Once you start resolving escalations, they'll archive here so you can revisit your replies."}
            </p>
        </div>
    );
}


/* iter98.99ef — Tier 2 chip-picker on Resolve.
 *
 * Each chip stamps an `action_kind` AND pre-fills the note with a sensible
 * default (the doctor can edit before sending). Picking a chip auto-creates
 * a typed PES task on the coach's dashboard so the loop closes with a
 * concrete TODO, not a vague text.
 *
 * Custom = legacy free-text behaviour, no auto-task except if a note is
 * provided (in which case backend creates a "custom" task with the note as
 * its label — matches the framework in iter98.99ee).
 */
const RESOLVE_CHIPS = [
    {
        kind: "continue_current",
        label: "Continue current plan",
        Icon: CheckCircle,
        tone: "emerald",
        prefill: "Continue the current plan as scheduled. Please reinforce wear-time guidance with the patient.",
        help: "Coach gets: \"Reinforce wear-time guidance with patient.\"",
    },
    {
        kind: "pause_wear",
        label: "Pause wear",
        Icon: Clock,
        tone: "amber",
        prefill: "Pause wear for [X] days. Please ask the patient to resume on [date] and confirm with me.",
        help: "Coach gets: \"Confirm wear paused as advised.\"",
    },
    {
        kind: "book_visit",
        label: "Book follow-up visit",
        Icon: ChatTeardropDots,
        tone: "primary",
        prefill: "Please coordinate a follow-up visit with the patient at the earliest convenient slot.",
        help: "Coach gets: \"Coordinate follow-up visit with patient.\"",
    },
    {
        kind: "order_replacement",
        label: "Order replacement aligner",
        Icon: ArrowsClockwise,
        tone: "violet",
        prefill: "Authorize a replacement aligner for the current stage. Coordinate delivery with the patient.",
        help: "Coach gets: \"Confirm replacement aligner delivery.\"",
    },
    {
        kind: "send_to_refinement",
        label: "Send to refinement",
        Icon: ShieldWarning,
        tone: "rose",
        prefill: "Refer to refinement queue. Plan revision needed based on this concern.",
        help: "Coach gets: \"Brief patient on refinement plan.\"",
    },
    {
        kind: "custom",
        label: "Custom note",
        Icon: ChatCircle,
        tone: "neutral",
        prefill: "",
        help: "Free-text — your message will be the coach's task.",
    },
];

const CHIP_TONE_CLS = {
    emerald: { idle: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100", active: "bg-emerald-600 border-emerald-600 text-white" },
    amber:   { idle: "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100",        active: "bg-amber-600 border-amber-600 text-white" },
    primary: { idle: "bg-primary/5 border-primary/20 text-primary hover:bg-primary/10",       active: "bg-primary border-primary text-primary-foreground" },
    violet:  { idle: "bg-violet-50 border-violet-200 text-violet-800 hover:bg-violet-100",    active: "bg-violet-600 border-violet-600 text-white" },
    rose:    { idle: "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100",            active: "bg-rose-600 border-rose-600 text-white" },
    neutral: { idle: "bg-secondary/40 border-border text-foreground hover:bg-secondary/70",   active: "bg-foreground border-foreground text-background" },
};


function ResolveDialog({ esc, onClose, onResolved }) {
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [pickedKind, setPickedKind] = useState(null);

    const pickChip = (chip) => {
        setPickedKind(chip.kind);
        // Only overwrite the textarea if it's empty OR matches a previous
        // chip prefill — so manual edits aren't trashed if the doctor
        // toggles between chips.
        setNote((current) => {
            const prevPrefill = RESOLVE_CHIPS.find((c) => c.prefill === current.trim())?.prefill;
            if (!current.trim() || prevPrefill !== undefined) {
                return chip.prefill;
            }
            return current;
        });
    };

    const submit = async () => {
        setSaving(true);
        try {
            const body = {
                note: note.trim() || null,
                ...(pickedKind ? { action_kind: pickedKind } : {}),
            };
            const r = await api.post(`/doctor/compliance-escalations/${esc.id}/resolve`, body);
            onResolved(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not resolve");
        } finally { setSaving(false); }
    };

    const pickedChip = pickedKind ? RESOLVE_CHIPS.find((c) => c.kind === pickedKind) : null;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="escalation-resolve-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle size={18} className="text-emerald-600" weight="fill" /> Resolve escalation
                    </DialogTitle>
                    <DialogDescription>
                        Pick an outcome to auto-create a typed task for the coach — or write a custom note.
                    </DialogDescription>
                </DialogHeader>

                {/* Original concern context */}
                <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2 text-[13px] text-muted-foreground" data-testid="escalation-resolve-original-note">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground/80 mb-1">CoE flagged</div>
                    {esc.note}
                </div>

                {/* Chip picker */}
                <div data-testid="escalation-resolve-chip-grid">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground mb-2">Choose outcome</div>
                    <div className="grid grid-cols-2 gap-2">
                        {RESOLVE_CHIPS.map((chip) => {
                            const isActive = pickedKind === chip.kind;
                            const cls = CHIP_TONE_CLS[chip.tone] || CHIP_TONE_CLS.neutral;
                            const Icon = chip.Icon;
                            return (
                                <button
                                    key={chip.kind}
                                    type="button"
                                    onClick={() => pickChip(chip)}
                                    title={chip.help}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] font-semibold border transition ${isActive ? cls.active : cls.idle}`}
                                    data-testid={`escalation-resolve-chip-${chip.kind}`}
                                >
                                    <Icon size={14} weight={isActive ? "fill" : "duotone"} />
                                    <span className="truncate">{chip.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {pickedChip && (
                        <div className="text-[11px] text-muted-foreground mt-2 italic" data-testid="escalation-resolve-chip-help">
                            {pickedChip.help}
                        </div>
                    )}
                </div>

                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    placeholder={pickedKind ? "Edit the auto-fill above…" : "Your reply / next steps (optional)…"}
                    className="w-full resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                    data-testid="escalation-resolve-note-input"
                />

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="escalation-resolve-submit"
                    >
                        {saving ? "Resolving…" : "Mark resolved"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
